import React, { useState } from "react";
import { User } from "lucide-react";
import { C, RARITY } from "../../constants/design";
import { INVENTORY_IMGS, SLOT, STATS, STAT_DISPLAY_ORDER } from "../../constants/items";
import { iName, iSkin, getStats } from "../../engine/itemEngine";
import type { Item, Player } from "../../types/game";
import { StatIcon } from "./StatIcon";

const TICKET_ICON = "https://i.postimg.cc/d3sX5KSp/ticket-foot-viol-(1).png";
const GOLD = "#D4AF37";

// ── Rarity-framed card (rarity color border only) ────────────────────────────
function RarityCard({
  rarity,
  children,
  style,
}: {
  rarity: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  const r = RARITY[rarity as keyof typeof RARITY] ?? RARITY["commun"];
  return (
    <div
      style={{
        borderRadius: 18,
        background: "linear-gradient(160deg,#0d1220 0%,#080c17 100%)",
        border: `2px solid ${r.color}`,
        boxShadow: `0 6px 24px ${r.glow}, inset 0 0 40px ${r.color}18`,
        overflow: "hidden",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ── Stats block ──────────────────────────────────────────────────────────────
function StatsBlock({ it }: { it: Item }) {
  const entries = (Object.entries(getStats(it)) as [string, number][])
    .filter(([k]) => k !== "energy" && k !== "blessure")
    .sort(([a], [b]) => STAT_DISPLAY_ORDER.indexOf(a) - STAT_DISPLAY_ORDER.indexOf(b));
  return (
    <div
      style={{
        background: "rgba(0,0,0,0.45)",
        borderRadius: 12,
        padding: "12px 14px",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        margin: "14px 14px 14px",
      }}
    >
      {entries.map(([key, val]) => {
        const sd = STATS.find((s) => s.key === key);
        if (!sd) return null;
        return (
          <div key={key} style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <StatIcon st={sd} size={20} />
            <span style={{ fontSize: 16, fontWeight: 900, color: "#fff" }}>+{val}</span>
            <span style={{ fontSize: 14, color: "rgba(255,255,255,0.5)" }}>{sd.label}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Rarity pill ───────────────────────────────────────────────────────────────
function RarityPill({ rarity }: { rarity: string }) {
  const r = RARITY[rarity as keyof typeof RARITY] ?? RARITY["commun"];
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 900,
        letterSpacing: 1,
        textTransform: "uppercase",
        color: "#000",
        background: r.color,
        borderRadius: 999,
        padding: "3px 10px",
        display: "inline-block",
      }}
    >
      {r.label}
    </span>
  );
}

// ── Preview card (step 3 & step 2 source reminder) ───────────────────────────
// Shows "before" (original item) in a circle on the left,
// "after" (cosmetic appearance) on the right with rarity badge.
function PreviewCard({
  source,
  target,
}: {
  source: Item;
  target: Item;
}) {
  const r = RARITY[source.rarity as keyof typeof RARITY] ?? RARITY["commun"];
  const originalImg = INVENTORY_IMGS[iName(source)];
  const cosmeticImg = INVENTORY_IMGS[target.name];
  const slotDef = SLOT[source.slot] as any;

  return (
    <RarityCard rarity={source.rarity}>
      {/* Top section: before circle (left) + after image (right) */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 0,
          padding: "20px 16px 12px",
        }}
      >
        {/* Left: original appearance in a circle */}
        <div
          style={{
            flex: "0 0 38%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              width: 88,
              height: 88,
              borderRadius: "50%",
              border: `2px solid ${r.color}55`,
              background: "rgba(255,255,255,0.04)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {originalImg ? (
              <img
                src={originalImg}
                alt=""
                style={{ width: 68, height: 68, objectFit: "contain", opacity: 0.65 }}
              />
            ) : (
              <span style={{ fontSize: 36, opacity: 0.4 }}>{slotDef?.emoji ?? "?"}</span>
            )}
          </div>
        </div>

        {/* Right: new appearance + rarity badge */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8,
          }}
        >
          {cosmeticImg ? (
            <img
              src={cosmeticImg}
              alt=""
              style={{
                width: 96,
                height: 96,
                objectFit: "contain",
                filter: `drop-shadow(0 6px 18px ${r.glow})`,
              }}
            />
          ) : (
            <span style={{ fontSize: 52 }}>{slotDef?.emoji ?? "?"}</span>
          )}
          <RarityPill rarity={source.rarity} />
        </div>
      </div>

      {/* Name */}
      <div
        style={{
          textAlign: "center",
          fontSize: 17,
          fontWeight: 900,
          color: "#fff",
          padding: "0 14px 8px",
        }}
      >
        {target.name}
      </div>

      {/* Appearance Changed tag */}
      <div style={{ display: "flex", justifyContent: "center", paddingBottom: 4 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "rgba(255,255,255,0.45)",
            background: "rgba(255,255,255,0.08)",
            borderRadius: 999,
            padding: "3px 12px",
          }}
        >
          ✨ Appearance Changed
        </span>
      </div>

      {/* Stats block */}
      <StatsBlock it={source} />
    </RarityCard>
  );
}

// ── Source reminder card (step 2 top) ────────────────────────────────────────
// Same card style but single item — shows the source item properly framed.
function SourceCard({ source }: { source: Item }) {
  const r = RARITY[source.rarity as keyof typeof RARITY] ?? RARITY["commun"];
  const img = INVENTORY_IMGS[iSkin(source)];
  const slotDef = SLOT[source.slot] as any;

  return (
    <RarityCard rarity={source.rarity}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          padding: "18px 16px 16px",
        }}
      >
        {img ? (
          <img
            src={img}
            alt=""
            style={{
              width: 84,
              height: 84,
              objectFit: "contain",
              filter: `drop-shadow(0 6px 16px ${r.glow})`,
            }}
          />
        ) : (
          <span style={{ fontSize: 48 }}>{slotDef?.emoji ?? "?"}</span>
        )}
        <RarityPill rarity={source.rarity} />
        <div style={{ fontSize: 16, fontWeight: 900, color: "#fff", textAlign: "center" }}>
          {iName(source)}
        </div>
      </div>
    </RarityCard>
  );
}

// ── Candidate tile (step 2 list) ─────────────────────────────────────────────
function CandidateTile({ it, onClick }: { it: Item; onClick: () => void }) {
  const r = RARITY[it.rarity as keyof typeof RARITY] ?? RARITY["commun"];
  const img = INVENTORY_IMGS[iSkin(it)];
  const slotDef = SLOT[it.slot] as any;
  const entries = (Object.entries(getStats(it)) as [string, number][])
    .filter(([k]) => k !== "energy" && k !== "blessure")
    .sort(([a], [b]) => STAT_DISPLAY_ORDER.indexOf(a) - STAT_DISPLAY_ORDER.indexOf(b));

  return (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        borderRadius: 14,
        border: `1.5px solid ${r.color}`,
        background: `linear-gradient(160deg,${r.color}12,${C.surface})`,
        boxShadow: `0 4px 16px ${r.glow}`,
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 14px",
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      {/* Item image */}
      <div
        style={{
          width: 56,
          height: 56,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(0,0,0,0.3)",
          borderRadius: 10,
          border: `1px solid ${r.color}33`,
        }}
      >
        {img ? (
          <img
            src={img}
            alt=""
            style={{ width: 46, height: 46, objectFit: "contain", filter: `drop-shadow(0 3px 8px ${r.glow})` }}
          />
        ) : (
          <span style={{ fontSize: 28 }}>{slotDef?.emoji ?? "?"}</span>
        )}
      </div>

      {/* Name + rarity + stats */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 800,
            color: "#fff",
            marginBottom: 3,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {iSkin(it)}
        </div>
        <div style={{ marginBottom: 5 }}>
          <RarityPill rarity={it.rarity} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {entries.map(([key, val]) => {
            const sd = STATS.find((s) => s.key === key);
            if (!sd) return null;
            return (
              <div key={key} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <StatIcon st={sd} size={11} />
                <span style={{ fontSize: 11, fontWeight: 700, color: "#fff" }}>+{val}</span>
                <span style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>{sd.short}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Arrow */}
      <span style={{ fontSize: 16, color: "rgba(255,255,255,0.3)", flexShrink: 0 }}>›</span>
    </button>
  );
}

// ── Step-1 grid tile ─────────────────────────────────────────────────────────
function SourceTile({
  it,
  isEquipped,
  onClick,
}: {
  it: Item;
  isEquipped: boolean;
  onClick: () => void;
}) {
  const r = RARITY[it.rarity as keyof typeof RARITY] ?? RARITY["commun"];
  return (
    <button
      onClick={onClick}
      style={{
        aspectRatio: "1",
        borderRadius: 12,
        border: `2px solid ${r.color}`,
        background: `linear-gradient(165deg,${r.color}15,${C.surface})`,
        boxShadow: `0 4px 14px ${r.glow}`,
        display: "grid",
        placeItems: "center",
        cursor: "pointer",
        position: "relative",
        overflow: "hidden",
        padding: 0,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(circle at 50% 50%,${r.color}22,transparent 70%)`,
          pointerEvents: "none",
        }}
      />
      {isEquipped && (
        <span
          style={{
            position: "absolute",
            top: 3,
            right: 3,
            width: 16,
            height: 16,
            borderRadius: "50%",
            background: "rgba(0,0,0,0.75)",
            border: "1px solid rgba(255,255,255,0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 4,
            pointerEvents: "none",
          }}
        >
          <User size={9} color="rgba(255,255,255,0.8)" />
        </span>
      )}
      {it.cosmetic && (
        <span
          style={{
            position: "absolute",
            top: 2,
            left: 3,
            fontSize: 9,
            zIndex: 4,
            lineHeight: 1,
            pointerEvents: "none",
            filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.8))",
          }}
        >
          ✨
        </span>
      )}
      {INVENTORY_IMGS[iSkin(it)] ? (
        <img
          src={INVENTORY_IMGS[iSkin(it)]}
          alt=""
          style={{ width: "80%", height: "80%", objectFit: "contain", position: "relative", zIndex: 2 }}
        />
      ) : (
        <span style={{ fontSize: 22, position: "relative", zIndex: 2 }}>
          {(SLOT[it.slot] as any)?.emoji ?? "?"}
        </span>
      )}
    </button>
  );
}

// ── Main modal ───────────────────────────────────────────────────────────────
export function ItemRedesignModal({
  me,
  onConfirm,
  onClose,
}: {
  me: Player;
  onConfirm: (sourceIid: string, targetIid: string) => void;
  onClose: () => void;
}) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [source, setSource] = useState<Item | null>(null);
  const [target, setTarget] = useState<Item | null>(null);

  const equippedIids = new Set(
    Object.values(me.equipped ?? {}).filter(Boolean) as string[]
  );
  const allItems = me.inventory ?? [];

  const candidates = source
    ? allItems.filter(
        (it) =>
          it.slot === source.slot &&
          it.iid !== source.iid &&
          !equippedIids.has(it.iid)
      )
    : [];

  const hasTicket = (me.tickets ?? 0) >= 1;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(0,0,0,0.88)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#0A0D1A",
          borderTop: `1px solid rgba(212,175,55,0.35)`,
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          padding: "16px 20px 40px",
          width: "100%",
          maxWidth: 430,
          maxHeight: "90vh",
          overflowY: "auto",
          color: "#fff",
        }}
      >
        {/* Handle */}
        <div
          style={{
            width: 40,
            height: 4,
            background: "rgba(255,255,255,0.18)",
            borderRadius: 999,
            margin: "0 auto 16px",
          }}
        />

        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            marginBottom: 14,
          }}
        >
          <div>
            <div style={{ fontSize: 17, fontWeight: 900 }}>✨ Item Redesign</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginTop: 3 }}>
              {step === 1 && "Step 1 of 3 — Select item to redesign"}
              {step === 2 && "Step 2 of 3 — Select new appearance"}
              {step === 3 && "Step 3 of 3 — Preview & confirm"}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 30,
              height: 30,
              borderRadius: "50%",
              border: "1px solid rgba(255,255,255,0.1)",
              background: "transparent",
              color: "rgba(255,255,255,0.45)",
              display: "grid",
              placeItems: "center",
              cursor: "pointer",
              fontSize: 18,
              flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        {/* Progress bar */}
        <div style={{ display: "flex", gap: 6, marginBottom: 22 }}>
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              style={{
                flex: 1,
                height: 3,
                borderRadius: 999,
                background: s <= step ? GOLD : "rgba(255,255,255,0.1)",
                transition: "background 0.2s",
              }}
            />
          ))}
        </div>

        {/* ── STEP 1 ── */}
        {step === 1 && (
          <div>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginBottom: 14, marginTop: 0 }}>
              Which item's appearance do you want to change?
            </p>
            {allItems.length === 0 ? (
              <p style={{ color: "rgba(255,255,255,0.35)", textAlign: "center", padding: "28px 0" }}>
                Your inventory is empty.
              </p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
                {allItems.map((it) => (
                  <SourceTile
                    key={it.iid}
                    it={it}
                    isEquipped={equippedIids.has(it.iid)}
                    onClick={() => { setSource(it); setTarget(null); setStep(2); }}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── STEP 2 ── */}
        {step === 2 && source && (
          <div>
            {/* Source reminder — same card style as step 3 */}
            <SourceCard source={source} />

            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", margin: "16px 0 12px" }}>
              Choose the look to apply — this item will be consumed:
            </p>

            {candidates.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  color: "rgba(255,255,255,0.35)",
                  padding: "28px 0",
                  fontSize: 13,
                }}
              >
                No other {(SLOT[source.slot] as any)?.label ?? source.slot} items in inventory.
                <br />
                <span style={{ fontSize: 12 }}>Open packs to get more items first.</span>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {candidates.map((it) => (
                  <CandidateTile
                    key={it.iid}
                    it={it}
                    onClick={() => { setTarget(it); setStep(3); }}
                  />
                ))}
              </div>
            )}

            <button
              onClick={() => { setStep(1); setSource(null); }}
              style={{
                marginTop: 20,
                width: "100%",
                padding: "12px",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.1)",
                background: "transparent",
                color: "rgba(255,255,255,0.5)",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              ← Back
            </button>
          </div>
        )}

        {/* ── STEP 3 ── */}
        {step === 3 && source && target && (
          <div>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginBottom: 14, marginTop: 0 }}>
              Your item will look like this:
            </p>

            <PreviewCard source={source} target={target} />

            {/* Consumed item note */}
            <div
              style={{
                marginTop: 14,
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12,
                color: "rgba(255,255,255,0.4)",
                background: "rgba(255,255,255,0.04)",
                borderRadius: 10,
                padding: "10px 12px",
              }}
            >
              <span style={{ fontSize: 15 }}>🗑️</span>
              <span>
                <strong style={{ color: "rgba(255,255,255,0.65)" }}>{iName(target)}</strong>
                {" "}({RARITY[target.rarity as keyof typeof RARITY]?.label ?? target.rarity}) will be consumed
              </span>
            </div>

            {/* Ticket cost */}
            <div
              style={{
                marginTop: 8,
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 12,
                color: hasTicket ? "rgba(255,255,255,0.4)" : "#ef4444",
                background: hasTicket ? "rgba(255,255,255,0.04)" : "rgba(239,68,68,0.08)",
                border: hasTicket ? "none" : "1px solid rgba(239,68,68,0.3)",
                borderRadius: 10,
                padding: "10px 12px",
              }}
            >
              <img src={TICKET_ICON} alt="" style={{ width: 18, height: 18, objectFit: "contain" }} />
              <span>
                {hasTicket ? (
                  <>Cost: <strong style={{ color: "#fff" }}>1 ticket</strong> — you have {me.tickets ?? 0}</>
                ) : (
                  "Not enough tickets — you need 1 ticket"
                )}
              </span>
            </div>

            {/* Action buttons */}
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button
                onClick={() => setStep(2)}
                style={{
                  flex: "0 0 40%",
                  padding: "15px",
                  borderRadius: 14,
                  border: "1px solid rgba(255,255,255,0.1)",
                  background: "transparent",
                  color: "rgba(255,255,255,0.55)",
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                ← Back
              </button>
              <button
                disabled={!hasTicket}
                onClick={() => {
                  if (!hasTicket) return;
                  onConfirm(source.iid, target.iid);
                  onClose();
                }}
                style={{
                  flex: 1,
                  padding: "15px",
                  borderRadius: 14,
                  border: "none",
                  background: hasTicket ? `linear-gradient(135deg,${GOLD},#b8962e)` : "rgba(255,255,255,0.07)",
                  color: hasTicket ? "#000" : "rgba(255,255,255,0.25)",
                  fontSize: 15,
                  fontWeight: 900,
                  cursor: hasTicket ? "pointer" : "not-allowed",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
              >
                ✨ Confirm
                <img src={TICKET_ICON} alt="" style={{ width: 18, height: 18, objectFit: "contain" }} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
